/**
 *
 * QuestionnaireQuestion
 *
 */

import React, { memo, FunctionComponent, useState } from 'react';
import { useIntl } from 'react-intl';
import messages from './messages';
import {
  EditorWrap,
  StyledEditor,
  StyledTransparentInputSM,
} from '../QuestionnaireEdit/styles';
import { Wrap } from '../../../../assets/styledAssets/grid';
import Button from '../../../../components/Button/Button.component';
import {
  CloseIcon,
  CopyIcon,
  DeleteIcon,
} from '../../../../components/Icons/Icons.component';
import {
  accentColorPrim,
  accentColorTertiary,
  fontColorSecond,
  red,
} from '../../../../assets/styledAssets/vars';
import { Input } from '@progress/kendo-react-inputs';
import {
  StyledFormField,
  StyledRadioButton,
} from '../../../../components/FormField/styles';
import { EditorTools } from '@progress/kendo-react-editor';
import { Question } from '../../questionnaires';
import {
  addQuestion,
  editAnswer,
  editQuestion,
  addAnswer,
  removeAnswer,
  removeQuestion,
  copyQuestion,
} from '../../Questionnaires.slice';
import { ClickAwayListener } from '@material-ui/core';
import { QuestionWrap } from './styles';
import RightActions from '../RightActions/RightActions.component';
import { addSection } from '../../Questionnaires.actions';

interface IProps {
  question: Question;
  index: number;
  seriesIndex: number;
  editQuestion: typeof editQuestion;
  editAnswer: typeof editAnswer;
  addSection: typeof addSection;
  addQuestion: typeof addQuestion;
  addAnswer: typeof addAnswer;
  removeAnswer: typeof removeAnswer;
  removeQuestion: typeof removeQuestion;
  copyQuestion: typeof copyQuestion;
}

const QuestionnaireQuestion: FunctionComponent<IProps> = ({
  question,
  index,
  seriesIndex,
  editAnswer,
  editQuestion,
  addSection,
  addQuestion,
  addAnswer,
  removeAnswer,
  removeQuestion,
  copyQuestion,
}) => {
  const intl = useIntl();
  const { Bold, Italic, Underline } = EditorTools;
  const [activeAnswer, setActiveAnswer] = useState<number | null>(null);
  return (
    <QuestionWrap>
      <EditorWrap>
        <StyledEditor
          tools={[[Bold, Italic, Underline]]}
          contentStyle={{ height: 120 }}
          value={question.question || question.text || ''}
          onChange={(e) =>
            editQuestion({
              seriesIdx: seriesIndex,
              questionIdx: index,
              question: { ...question, question: e.html },
            })
          }
        />
        <Wrap
          className={'editor-toolbar-controls'}
          width={'6rem'}
          alignItems={'center'}
          justifyCont={'space-between'}
        >
          <Button
            icon
            transparent
            onClick={() =>
              copyQuestion({ seriesIdx: seriesIndex, questionIdx: index })
            }
          >
            <CopyIcon color={accentColorPrim} width={'1.8rem'} />
          </Button>
          <Button
            icon
            transparent
            onClick={() =>
              removeQuestion({ seriesIdx: seriesIndex, questionIdx: index })
            }
          >
            <DeleteIcon color={red} width={'1.8rem'} />
          </Button>
        </Wrap>
        <Wrap className={'editor-body'}>
          <StyledTransparentInputSM>
            <Input
              placeholder={intl.formatMessage(messages.code)}
              value={question.code || ''}
              onChange={(e) =>
                editQuestion({
                  seriesIdx: seriesIndex,
                  questionIdx: index,
                  question: { ...question, code: e.value },
                })
              }
            />
          </StyledTransparentInputSM>
          <StyledTransparentInputSM>
            <Input
              placeholder={intl.formatMessage(messages.descriptionOpt)}
              value={question.preText || ''}
              onChange={(e) =>
                editQuestion({
                  seriesIdx: seriesIndex,
                  questionIdx: index,
                  question: { ...question, preText: e.value },
                })
              }
            />
          </StyledTransparentInputSM>
          {(question.answers || []).map((answer, answerIdx) => (
            <Wrap
              key={`answer-${answerIdx}`}
              width={'100%'}
              alignItems={'center'}
              margin={'2rem 0'}
            >
              {activeAnswer !== answerIdx ? (
                <Wrap
                  br={'1.2rem'}
                  width={'calc(100% - 21rem)'}
                  alignItems={'center'}
                  bgColor={accentColorTertiary}
                  padding={'1.5rem'}
                  onClick={() => setActiveAnswer(answerIdx)}
                >
                  <StyledRadioButton />
                  <Wrap margin={'0 0 0 1.2rem'} width={'calc(100% - 4rem)'}>
                    {answer.text}
                  </Wrap>
                </Wrap>
              ) : (
                <ClickAwayListener onClickAway={() => setActiveAnswer(null)}>
                  <Wrap width={'calc(100% - 21rem)'}>
                    <StyledFormField>
                      <Input
                        value={answer.text}
                        onChange={(e) =>
                          editAnswer({
                            seriesIdx: seriesIndex,
                            questionIdx: index,
                            answerIdx: answerIdx,
                            answer: { ...answer, text: e.value },
                          })
                        }
                      />
                    </StyledFormField>
                  </Wrap>
                </ClickAwayListener>
              )}
              <Wrap
                br={'1.2rem'}
                width={'12rem'}
                bgColor={accentColorTertiary}
                margin={'0 2rem'}
              >
                <StyledFormField grey>
                  <Input
                    type="number"
                    value={answer.score}
                    onChange={(e) =>
                      editAnswer({
                        seriesIdx: seriesIndex,
                        questionIdx: index,
                        answerIdx: answerIdx,
                        answer: { ...answer, score: parseFloat(e.value) },
                      })
                    }
                  />
                </StyledFormField>
              </Wrap>
              <Button
                icon
                transparent
                onClick={() =>
                  removeAnswer({
                    seriesIdx: seriesIndex,
                    questionIdx: index,
                    answerIdx: answerIdx,
                  })
                }
              >
                <CloseIcon color={fontColorSecond} width={'1.8rem'} />
              </Button>
            </Wrap>
          ))}
          <Wrap
            margin={'2rem 1rem 0 1rem'}
            color={accentColorPrim}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              addAnswer({ seriesIdx: seriesIndex, questionIdx: index });
              setActiveAnswer(question.answers.length);
            }}
          >
            {intl.formatMessage(messages.addAnswer)}
          </Wrap>
        </Wrap>
      </EditorWrap>
      <RightActions
        addSection={addSection}
        addQuestion={addQuestion}
        sectionIdx={seriesIndex + 1}
        questionIdx={index + 1}
      />
    </QuestionWrap>
  );
};

export default memo(QuestionnaireQuestion);
